import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ActionButton, FooterButton } from '@rivethealth/data/buttons';
import { Option, OptionsSet } from '@rivethealth/data/options';
import * as _ from 'lodash';
import { get } from 'lodash';
import { ObservableInput } from 'observable-input';
import {
  BehaviorSubject,
  combineLatest,
  merge,
  Observable,
  of,
  Subject,
} from 'rxjs';
import {
  filter,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AnalyticsEventOutput } from '../analytics/common-analytics.service';
import { AnchoredPosition } from '../callout/callout-position';
import { ChangeService } from '../core/change.service';
import { Loadable, success } from '../helpers/loadable';
import { replayLast } from '../rx/util';
import { OptionSelectNodeDirective } from './option-select-node.directive';
import {
  OptionSelectComponent,
  OptionsLoader,
  PinSetting,
} from './option-select.component';
import { getOptionDisplay, getOptionsDisplay } from './options';
import { RecentOptionsService } from './recent-dropdown-options.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'select-dropdown',
  host: { '[class.inline]': 'hostInline' },
  styleUrls: ['./select-dropdown.component.scss'],
  templateUrl: './select-dropdown.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SelectDropdownComponent),
    },
    ChangeService,
  ],
})
export class SelectDropdownComponent<T = unknown> implements OnInit {
  constructor(
    private readonly recentOptionsService: RecentOptionsService,
    private readonly changeService: ChangeService,
  ) {}
  private readonly destroy = new Subject();

  @ViewChild('dropdown', { static: false })
  readonly dropdownRef: ElementRef<HTMLElement>;

  @ViewChild(OptionSelectComponent, { static: false })
  readonly optionSelect: OptionSelectComponent;

  @ViewChild('callout', { static: false })
  readonly calloutRef: ElementRef<HTMLElement>;

  @ContentChild(OptionSelectNodeDirective, { static: false })
  readonly nodeDirective: OptionSelectNodeDirective<T>;

  @Output() readonly filter = new Subject<string>();

  @Input() error = false;

  @Input() warning = false;

  @Input() recentOptionsIdentifier: string | null = null;

  @Input() forceSortOnBestMatch: boolean = false;

  @Input() all: boolean = false;

  @Input()
  selectedEnabled: boolean = true;

  @Input()
  allowSelectingEmpty: boolean = true;

  @Input('optionActionButton')
  optionActionButton: ActionButton;

  @Input()
  readonly analyticsEvents: {
    openDropdown?: AnalyticsEventOutput<void>;
    select?: AnalyticsEventOutput<string>;
    multiSelect?: AnalyticsEventOutput<string[]>;
    click?: AnalyticsEventOutput<void>;
  } = {};

  @ObservableInput()
  @Input('disabled')
  private readonly _disabled!: Observable<boolean>;
  readonly disabledChange = new Subject<boolean>();
  readonly defaultedDisabled = merge(this.disabledChange, this._disabled).pipe(
    startWith(false),
  );

  @ObservableInput()
  @Input('inline')
  private readonly _inline!: Observable<boolean>;
  readonly defaultedInline = this._inline.pipe(startWith(false));

  @ObservableInput()
  @Input('wideOptions')
  private readonly _wideOptions!: Observable<boolean>;
  readonly defaultedWideOptions = this._wideOptions.pipe(startWith(false));

  @Input()
  combinedRight: boolean = false;

  @Input()
  combinedLeft: boolean = false;

  @ObservableInput()
  @Input('required')
  private readonly _required!: Observable<boolean>;
  readonly defaultedRequired = this._required.pipe(startWith(false));

  @ObservableInput()
  @Input('autoFocus')
  readonly _autoFocus!: Observable<boolean>;
  readonly defaultedAutoFocus = this._autoFocus.pipe(startWith(false));

  @ObservableInput()
  @Input('exact')
  private readonly _exact!: Observable<boolean>;
  readonly defaultedExact = this._exact.pipe(startWith(false));

  @ObservableInput()
  @Input('filterable')
  private readonly _filterable!: Observable<boolean>;
  readonly defaultedFilterable: Observable<boolean> = this._filterable.pipe(
    startWith(false),
  );

  @ObservableInput()
  @Input('placeholder')
  private readonly _placeholder!: Observable<string | null>;
  readonly defaultedPlaceholder: Observable<string | null> =
    this._placeholder.pipe(startWith(<string | null>null));

  @ObservableInput()
  @Input('label')
  private readonly _label!: Observable<string | null>;
  readonly defaultedLabel: Observable<string | null> = this._label.pipe(
    startWith(<string | null>null),
  );

  @ObservableInput()
  @Input('labelDirection')
  private readonly _labelDirection!: Observable<'row' | 'column' | null>;
  readonly defaultedLabelDirection: Observable<'row' | 'column' | null> =
    this._labelDirection.pipe(startWith(<'row' | 'column' | null>null));

  @Input()
  pinned!: PinSetting[];

  @ObservableInput()
  @Input('help')
  private readonly _help!: Observable<string | null>;
  readonly defaultedHelp: Observable<string | null> = this._help.pipe(
    startWith(<any>null),
  );

  @ObservableInput()
  @Input('display')
  private readonly _display!: Observable<string | null>;
  defaultedDisplay: Observable<string | null> = this._display.pipe(
    startWith(<string | null>null),
  );

  @ObservableInput()
  @Input('loadable')
  readonly _loadable!: Observable<Loadable<any> | null>;
  readonly defaultedLoadable = this._loadable.pipe(startWith(null));

  @ObservableInput()
  @Input('options')
  readonly _options!: Observable<Option[]>;
  readonly defaultedOptions: Observable<Option[]> = this._options.pipe(
    startWith([]),
    map(options => options || []),
  );

  @ObservableInput()
  @Input('tabbedOptions')
  readonly _tabbedOptions!: Observable<OptionsSet[]>;
  readonly defaultedTabbedOptions: Observable<OptionsSet[]> =
    this._tabbedOptions.pipe(startWith([]));

  @ObservableInput()
  @Input('optionsLoader')
  readonly optionsLoader!: Observable<OptionsLoader>;
  readonly defaultedOptionsLoader: Observable<OptionsLoader | null> =
    this.optionsLoader.pipe(startWith(null));

  @Input()
  dragScrollable: boolean = false;

  @Input()
  displayId: boolean;

  @Output()
  readonly onPinnedEvent = new EventEmitter();

  @Input()
  maxSelectedCount: number | null = null;

  readonly tabSubject = new BehaviorSubject<string>('');

  readonly tabOptions = combineLatest([
    this.defaultedTabbedOptions,
    this.tabSubject,
  ]).pipe(
    map(
      ([optionsSet, tab]) =>
        optionsSet.find(options => options.name === tab)?.options || [],
    ),
  );

  readonly tabNames = this.defaultedTabbedOptions.pipe(
    map(options => options.map(option => option.name)),
  );

  @ObservableInput()
  @Input('minSelectedCount')
  readonly _minSelectedCount!: Observable<number>;
  readonly defaultedMinSelectedCount: Observable<number> =
    this._minSelectedCount.pipe(startWith(0));

  @ObservableInput()
  @Input('hideCount')
  readonly _hideCount!: Observable<boolean>;
  readonly defaultHideCount: Observable<boolean> = this._hideCount.pipe(
    startWith(false),
  );

  @Input()
  draggable: boolean = false;

  @Output()
  onDragEvent = new EventEmitter<[number, number]>();

  @Input()
  readonly showAvatars: boolean = false;

  readonly usingLoader: Observable<boolean> = this.defaultedOptionsLoader.pipe(
    map(loader => !!loader),
  );

  cachedOptions: {
    count: number;
    filter: string | null;
    loadable: Loadable<{ count: number; options: Option[] }>;
  } | null = null;

  readonly wrappedOptionsLoader: Observable<OptionsLoader | null> =
    this.defaultedOptionsLoader.pipe(
      map(loader => {
        return loader
          ? (
              count: number,
              filter: string | null,
              ids: string[] | null,
            ): Observable<
              Loadable<{
                options: Option[];
                count: number;
              }>
            > => {
              if (
                this.cachedOptions &&
                this.cachedOptions.count === count &&
                this.cachedOptions.filter === filter &&
                ids === null
              ) {
                return of(this.cachedOptions.loadable);
              }
              return loader(count, filter, ids).pipe(
                tap(loadable => {
                  if (!loadable.loading && !loadable.erred && loadable.data) {
                    this.cachedOptions = {
                      count,
                      filter,
                      loadable,
                    };
                  }
                }),
              );
            }
          : null;
      }),
    );

  @Input() readonly defaultToLastSelected: boolean = false;
  private readonly lastSelected = new BehaviorSubject<string | null>(null);

  @ObservableInput()
  @Input('multiSelect')
  readonly _multiSelect!: Observable<boolean>;
  readonly defaultedMultiSelect: Observable<boolean> = this._multiSelect.pipe(
    startWith(false),
  );

  @Input()
  readonly multiSelectButtons: boolean = true;

  @Input()
  alwaysShowSelectAll: boolean = false;

  @Input('selectAllButton')
  @ObservableInput()
  private readonly _selectAllButton!: Observable<FooterButton>;

  @Input('leftButtons')
  @ObservableInput()
  private readonly _leftButtons!: Observable<FooterButton[]>;
  private readonly defaultLeftButtons = this._leftButtons.pipe(
    map(buttons => (buttons ? buttons : [])),
    startWith([]),
  );
  leftButtons: Observable<FooterButton[]> = this.defaultLeftButtons.pipe(
    withLatestFrom(
      this.defaultedMultiSelect,
      this._selectAllButton.pipe(startWith(null)),
      this.defaultedOptions,
    ),
    map(([buttons, multi, selectAll, options]) => {
      const selectAllButton = {
        text: 'Select all',
        onClick: () => this.selectAllTrigger.next(null),
      };
      return multi && this.multiSelectButtons
        ? selectAll
          ? [...buttons, selectAll]
          : (options.length >= 100 && !this.alwaysShowSelectAll) ||
              this.maxSelectedCount !== null
            ? buttons
            : [...buttons, selectAllButton]
        : buttons;
    }),
  );

  @Input('rightButtons')
  @ObservableInput()
  private readonly _rightButtons!: Observable<FooterButton[]>;
  private readonly defaultRightButtons = this._rightButtons.pipe(
    map(buttons => (buttons ? buttons : [])),
    startWith([]),
  );
  rightButtons: Observable<FooterButton[]> = this.defaultRightButtons.pipe(
    withLatestFrom(this.defaultedMultiSelect),
    map(([buttons, multi]) => {
      const clearAllButton = {
        text: 'Clear',
        onClick: () => this.selectedOptionIdsChange.next([]),
      };
      return multi && this.multiSelectButtons
        ? [...buttons, clearAllButton]
        : buttons;
    }),
  );

  reversedRightButtons = this.rightButtons.pipe(
    map(buttons => buttons.slice().reverse()),
  );

  @Output()
  readonly selectedOptionIdChange = new Subject<string>();

  @Output()
  readonly selectedOptionIdsChange = new Subject<string[]>();

  @Output()
  readonly onClose = new EventEmitter();

  readonly valueIdChange = new Subject<string>();
  readonly valueIdsChange = new Subject<string[]>();

  @ObservableInput()
  @Input('selectedOptionId')
  readonly _selectedOptionId!: Observable<string>;
  readonly defaultedSelectedOptionId = combineLatest([
    merge(
      this._selectedOptionId,
      this.selectedOptionIdChange,
      this.valueIdChange,
    ),
    this.lastSelected,
    this.defaultedMultiSelect,
  ]).pipe(
    filter(([, , multiSelect]) => !multiSelect),
    map(([id, lastId]) => id || lastId),
    startWith(null),
  );

  @ObservableInput()
  @Input('selectedOptionIds')
  readonly _selectedOptionIds!: Observable<string[]>;
  readonly defaultedSelectedOptionIds = combineLatest([
    merge(
      this._selectedOptionIds,
      this.selectedOptionIdsChange,
      this.valueIdsChange,
    ),
    this.defaultedMultiSelect,
  ]).pipe(
    filter(([_, multiSelect]) => !!multiSelect),
    map(([ids, _]) => ids),
    startWith(null),
  );

  readonly selectedOptionIdChangeSideEffects = this.selectedOptionIdChange.pipe(
    tap(id => this.propagateChange(id)),
    tap(id => {
      if (this.analyticsEvents.select) {
        this.analyticsEvents.select(id);
      }
    }),
    tap(() => this.close()),
  );

  readonly selectedOptionIdsChangeSideEffects =
    this.selectedOptionIdsChange.pipe(
      tap(ids => this.propagateChange(ids)),
      tap(ids => {
        if (this.analyticsEvents.multiSelect) {
          this.analyticsEvents.multiSelect(ids);
        }
      }),
    );

  private combinedSelectedIds = combineLatest([
    merge(this.defaultedSelectedOptionId, this.selectedOptionIdChange),
    merge(this.defaultedSelectedOptionIds, this.selectedOptionIdsChange),
    this.defaultedMultiSelect,
  ]).pipe(
    map(([id, ids, multi]) => (multi ? ids || [] : id === null ? [] : [id])),
    replayLast(),
  );

  readonly options = combineLatest([
    this.defaultedOptions,
    this.tabOptions,
  ]).pipe(
    map(([options, tabOptions]) =>
      tabOptions.length > 0 ? tabOptions : options,
    ),
  );

  readonly availableOptions = combineLatest([
    this.defaultedOptions,
    this.defaultedTabbedOptions,
  ]).pipe(
    map(([options, tabOptions]) =>
      options.length > 0
        ? options
        : tabOptions.reduce(
            (acc: Option[], subOptions: OptionsSet) =>
              acc.concat(subOptions.options),
            [],
          ),
    ),
  );

  readonly selectedOptionsLoadable = combineLatest([
    this.combinedSelectedIds,
    this.usingLoader,
    this.defaultedOptionsLoader,
    this.availableOptions,
  ]).pipe(
    switchMap(([ids, usingLoader, loader, options]) => {
      if (!usingLoader || ids.length === 0) {
        return of({
          loading: false,
          erred: false,
          data: {
            count: ids.length,
            options: options.filter(o => ids.indexOf(String(o.id)) !== -1),
          },
        });
      }
      const availableOptions: Option[] = _.get(
        this.cachedOptions,
        'loadable.data.options',
        [],
      ).filter((o: Option) => ids.indexOf(String(o.id)) !== -1);
      if (ids.length === availableOptions.length) {
        return of({
          loading: false,
          erred: false,
          data: {
            count: ids.length,
            options: availableOptions,
          },
        });
      }
      return loader!(ids.length, '', ids);
    }),
    replayLast(),
  );

  readonly selectedOptions = this.selectedOptionsLoadable.pipe(success());

  readonly selectedDisplay = combineLatest([
    this.selectedOptions,
    this.defaultedDisplay,
  ]).pipe(
    withLatestFrom(this.defaultedMultiSelect),
    map(([[options, display], multiSelect]) => {
      if (this.all) {
        return 'All';
      }
      if (display) return display; // display is an override
      if (multiSelect) {
        const ids = options.options.map(o => String(o.id));

        return getOptionsDisplay(ids, options.options);
      } else {
        if (options.options.length > 0) {
          return getOptionDisplay(options.options[0]);
        }
        return '';
      }
    }),
    replayLast(),
  );

  readonly selectedAuto: Observable<boolean> = combineLatest([
    this.selectedOptions,
    this.defaultedDisplay,
  ]).pipe(
    withLatestFrom(this.defaultedMultiSelect),
    map(([[options, display], multiSelect]) => {
      if (display || multiSelect) return false;
      // Don't display an auto badge for overridden display or multi-select
      else {
        return options.options.length > 0 ? options.options[0].auto! : false;
      }
    }),
    replayLast(),
  );

  selectedCount: Observable<number> = merge(
    this.selectedOptionIdsChange,
    this.defaultedSelectedOptionIds,
  ).pipe(
    map(selectedIds => {
      return get(selectedIds, 'length', 0);
    }),
  );

  get dropdownWidth() {
    return this.dropdownRef.nativeElement.offsetWidth;
  }

  readonly doOpen = new BehaviorSubject<boolean>(false);

  @ObservableInput()
  @Input('dismiss')
  private readonly dismiss!: Observable<number>;

  readonly openSubject = merge(
    this.doOpen,
    this.dismiss.pipe(
      map(() => {
        return false;
      }),
    ),
  );

  public close() {
    this.doOpen.next(false);
    this.onClose.emit();
  }

  public open() {
    this.doOpen.next(true);
    this.scrollToSelectedOption();
  }

  private scrollToSelectedOption() {
    if (this.optionSelect) {
      this.optionSelect.scrollSelectedIntoView();
    } else {
      setTimeout(() => {
        if (this.optionSelect) {
          this.optionSelect.scrollSelectedIntoView();
        }
      }, 0);
    }
  }

  isOpen = false;

  @Output()
  closeEvent = new EventEmitter();

  private readonly openSideEffects = this.openSubject.pipe(
    tap(isOpen => {
      if (!isOpen) {
        if (this.dropdownRef) {
          this.dropdownRef.nativeElement.focus();
        }
        this.closeEvent.emit();
        this.filter.next('');
      }
      this.isOpen = isOpen;
      if (this.analyticsEvents.openDropdown && isOpen) {
        this.analyticsEvents.openDropdown();
      }
    }),
  );

  readonly position = new BehaviorSubject<AnchoredPosition | null>(null);

  readonly positionedTop = this.position.pipe(
    map(position => {
      return (
        position === AnchoredPosition.TOP_RIGHT ||
        position === AnchoredPosition.TOP_LEFT
      );
    }),
  );

  readonly topAndBottomPositions = [
    AnchoredPosition.BOTTOM_RIGHT,
    AnchoredPosition.BOTTOM_LEFT,
    AnchoredPosition.TOP_RIGHT,
    AnchoredPosition.TOP_LEFT,
  ];

  public onCalloutFocusOut() {
    this.close();
  }

  private forwardKeyPressToOptions($event: KeyboardEvent) {
    if (this.optionSelect) {
      this.optionSelect.externalKeyPress.next($event.key);
    } else {
      setTimeout(() => {
        if (this.optionSelect) {
          this.optionSelect.externalKeyPress.next($event.key);
        }
      }, 0);
    }
  }

  @HostListener('keyup', ['$event'])
  @HostListener('keydown', ['$event'])
  keyUp($event: any) {
    const key = $event.key;
    if (
      key === 'ArrowDown' ||
      key === 'ArrowUp' ||
      key === 'Enter' ||
      key === ' '
    ) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter') {
      if ($event.type === 'keydown') {
        if (this.isOpen === true) {
          this.forwardKeyPressToOptions($event);
        } else {
          this.open();
        }
      }
    }
    if (key.length === 1) {
      if ($event.type === 'keydown') {
        this.open();
        this.forwardKeyPressToOptions($event);
      }
    }
    if (key === 'Escape') {
      if ($event.type === 'keydown') {
        this.close();
      }
    }
    if (key === 'Tab') {
      if ($event.type === 'keydown') {
        if (this.isOpen === true) {
          this.forwardKeyPressToOptions($event);
        }
      }
    }
  }

  hostInline: boolean = false;

  private propagateChange = (_: any) => {};

  writeValue(value: any) {
    this.valueChange.next(value);
  }

  private valueChange = new Subject<string | string[]>();

  private readonly valueChangeHandler = this.valueChange.pipe(
    withLatestFrom(this.defaultedMultiSelect),
    tap(([value, multiSelect]) => {
      if (multiSelect) {
        this.valueIdsChange.next(value as string[]);
      } else {
        this.valueIdChange.next(value as string);
      }
    }),
  );

  registerOnChange(fn: (_: any) => {}) {
    this.propagateChange = fn;
  }

  registerOnTouched() {}

  setDisabledState(disabled: boolean) {
    this.disabledChange.next(disabled);
  }

  ngOnInit() {
    const recentIds = this.recentOptionsService.getLocalLimited(
      this.recentOptionsIdentifier,
    );

    if (this.defaultToLastSelected && recentIds != null) {
      this.selectedOptionIdChange.next(recentIds[0]);
    }

    this.changeService.listen(this.tabNames, v => {
      if (v.length > 0) {
        this.tabSubject.next(v[0]);
      }
    });

    this.defaultedInline
      .pipe(takeUntil(this.destroy))
      .subscribe(inline => (this.hostInline = inline));
  }

  ngOnDestroy() {
    this.destroy.next(undefined);
  }

  selectAllTrigger = new Subject<null>();

  selectAll = this.selectAllTrigger.pipe(
    withLatestFrom(this.defaultedOptions),
    map(([_, options]) =>
      this.selectedOptionIdsChange.next(
        options.map(option => option.id.toString()),
      ),
    ),
  );

  readonly subs = merge(
    this.openSideEffects.pipe(
      map(() => {
        return true;
      }),
    ),
    this.selectedOptionIdChangeSideEffects.pipe(
      map(() => {
        return true;
      }),
    ),
    this.selectedOptionIdsChangeSideEffects.pipe(
      map(() => {
        return true;
      }),
      tap(() => {
        if (this.analyticsEvents.click) {
          this.analyticsEvents.click();
        }
      }),
    ),
    this.valueChangeHandler.pipe(
      map(() => {
        return true;
      }),
    ),
    this.selectAll,
  );
}
